import { observer } from "mobx-react";
import ListLayout from "../../../../components/layouts/list-layout/list-layout";
import customizationStore from "../../../../stores/customizations/customization-store";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import { useCallback, useEffect, useState } from "react";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import useNavigationBlocker from "../../../../hooks/navigation-blocker-hook";
import { ConfirmModal } from "../../../../components/modal/components/confirm-modal/confirm-modal";
import '../../look-and-feel.scss';
import GeneralCard from './general-card/general-card';
import BackgroundCard from './background-card/background-card';
import currentPortalStore from "../../../../stores/current-portal-store";
import useExpandCollapse from '../../../../hooks/expand-collapse-hook';
import PortalEdgeBackgroundCard from "./background-card/portal-edge-background-card";

const ReturnPortalSettingsEditorPage: React.FC = observer(() => {

    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    const { setSubTitles } = usePage();

    const expandCollapseStore = useExpandCollapse(false);

    useEffect(() => {
        const params = new URLSearchParams(document.location.search);

        const cardFocusKey = params.get('card_focus');
        if (cardFocusKey) {
            expandCollapseStore.setExpanded(cardFocusKey, true);
        }
    }, [expandCollapseStore]);

    useEffect(() => {
        setSubTitles(['Portal', 'Look and Feel', 'Return portal settings']);
    }, [setSubTitles]);

    const hasChanges = () => {
        return customizationStore.hasChanges() || !!currentPortalStore.setting?.hasChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        customizationStore.discardChanges();
        currentPortalStore.setting?.discardChanges();
    }

    const onSaveClicked = () => {
        const promised = [];

        if (hasChanges()) {
            promised.push(
                customizationStore.save()
            );
        }

        if (currentPortalStore.setting?.hasChanges()) {
            promised.push(
                currentPortalStore.setting?.save()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
        })
    }

    return (
        <>
            <EditorPageLayout toolbar={{
                hasLocalesSelector: false,
                saveButton: {
                    hasChanges: hasChanges(),
                    disabled: !hasChanges(),
                    onClick: onSaveClicked
                },
            }}>
                <ListLayout>
                    <GeneralCard
                        onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('general_settings', v)}
                        isExpanded={expandCollapseStore.getIsExpanded('general_settings')}
                    />
                     {
                        currentPortalStore.setting?.enablePortalV2 ? (
                            <PortalEdgeBackgroundCard
                                onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('background_settings', v)}
                                isExpanded={expandCollapseStore.getIsExpanded('background_settings')}
                            />
                        ) : (
                            <BackgroundCard
                                onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('background_settings', v)}
                                isExpanded={expandCollapseStore.getIsExpanded('background_settings')}
                            />
                        )
                     }
                </ListLayout>
            </EditorPageLayout>

            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
        </>
    );
});

export default ReturnPortalSettingsEditorPage;
